import React, { ReactNode } from "react";
import { Navigate } from "react-router-dom";

import paths from "./paths";
import { Redirect } from "./redirect";

type RouteAuthType =
  | "guest-only"
  | "require-team-owner" // includes email verified
  | "required";

export type RouteType = {
  auth?: RouteAuthType;
  authRequiredRedirectTo?: "login" | "signup";
  children?: Record<string, RouteType>;
  component: ReactNode;
};

const AuthEmailVerificationTokenPage = React.lazy(
  () => import("common/auth/guard/email-verification/token"),
);
const AuthLoginPage = React.lazy(() => import("pages/auth/login"));
const AuthPasswordPage = React.lazy(() => import("pages/auth/password"));
const AuthPasswordTokenPage = React.lazy(
  () => import("pages/auth/password/token"),
);
const AuthSignupMemberPage = React.lazy(
  () => import("pages/auth/signup-member"),
);
const AuthSignupPage = React.lazy(() => import("pages/auth/signup"));
const BonusTokenPage = React.lazy(() => import("pages/auth/bonus"));
const ChargeInvoicePage = React.lazy(
  () => import("pages/charge/charge-invoice"),
);
const ExtensionLoginPage = React.lazy(() => import("pages/extension/login"));
const ExtensionOnboardingPage = React.lazy(
  () => import("pages/extension/onboarding"),
);
const ExtensionUninstallPage = React.lazy(
  () => import("pages/extension/uninstall"),
);
const IntegrationsPage = React.lazy(() => import("pages/integrations"));
const InvoiceIdPage = React.lazy(() => import("pages/invoice/id"));
const InvoicePayNowPage = React.lazy(() => import("pages/invoice/pay-now"));
const OptOutPage = React.lazy(() => import("pages/opt-out"));
const OptOutDomainTokenPage = React.lazy(
  () => import("pages/opt-out/domain/token"),
);
const OptOutEmailTokenPage = React.lazy(
  () => import("pages/opt-out/email/token"),
);
const PurchaseCheckoutPage = React.lazy(
  () => import("pages/purchase/checkout"),
);
const PurchasePage = React.lazy(() => import("pages/purchase/index"));
const PurchaseReturnStripePage = React.lazy(
  () => import("pages/purchase/return/stripe"),
);
const SearchBulkPage = React.lazy(
  () => import("pages/search-bulk/search-bulk"),
);
const SearchSinglePage = React.lazy(() => import("pages/search-single"));
const SettingsPage = React.lazy(() => import("pages/settings/page"));
const SettingsAccountPage = React.lazy(() => import("pages/settings/account"));
const SettingsApiPage = React.lazy(() => import("pages/settings/api"));
const SettingsAuthPage = React.lazy(() => import("pages/settings/auth"));
const SettingsBillingPage = React.lazy(() => import("pages/settings/billing"));
const SettingsBillingTrialConvert = React.lazy(
  () => import("pages/settings/billing-trial-convert"),
);
const SettingsDeletePage = React.lazy(() => import("pages/settings/delete"));
const SettingsIntegrationPage = React.lazy(
  () => import("pages/settings/integration"),
);
const SettingsTeamPage = React.lazy(() => import("pages/settings/team"));
const SettingsUsagePage = React.lazy(() => import("pages/settings/usage"));
const VerifyPage = React.lazy(() => import("pages/verify"));
const MapExtractorPage = React.lazy(
  () => import("pages/search-bulk/extract-map"),
);
const EmailUnsubscribePage = React.lazy(
  () => import("pages/settings/email-unsubscribe"),
);

const routeList: Record<string, RouteType> = {
  "/": { component: <Navigate to={paths.searchSingle()} /> },
  "/auth/login": { auth: "guest-only", component: <AuthLoginPage /> },
  "/auth/invites/:token": { component: <AuthSignupMemberPage /> },
  "/bonus/:token": { component: <BonusTokenPage /> },
  "/opt_out": { component: <OptOutPage /> },
  "/opt_out/domain/:token": { component: <OptOutDomainTokenPage /> },
  "/opt_out/email/:token": { component: <OptOutEmailTokenPage /> },
  "/sign_up": { auth: "guest-only", component: <AuthSignupPage /> },
  "/sign_up/verify": { component: <Navigate to={paths.searchSingle()} /> },
  "/sign_up/verify/email/confirm": {
    component: <AuthEmailVerificationTokenPage />,
  },
  "/sign_up/verify/email/confirm/:token": {
    component: <AuthEmailVerificationTokenPage />,
  },
  "/auth/password": { component: <AuthPasswordPage /> },
  "/auth/password/:token": { component: <AuthPasswordTokenPage /> },
  "/billing/charge/:charge_id/invoice": { component: <ChargeInvoicePage /> },
  "/extension/login": { component: <ExtensionLoginPage /> },
  "/extension/onboarding": { component: <ExtensionOnboardingPage /> },
  "/extension/uninstall": { component: <ExtensionUninstallPage /> },
  "/invoice/:id/failed": { component: <InvoiceIdPage /> },
  "/search/single": {
    component: <SearchSinglePage />,
  },
  "/search/bulk": {
    component: <SearchBulkPage />,
  },
  "/search/bulk/new": {
    auth: "required",
    component: <SearchBulkPage createModalOpen />,
  },
  "/search/bulk/:search_id": {
    auth: "required",
    component: <SearchBulkPage />,
  },
  "/search/bulk/:search_id/download": {
    auth: "required",
    component: <SearchBulkPage />,
  },
  "/search/bulk/:search_id/map-columns": {
    auth: "required",
    component: <SearchBulkPage mapColumnsOpen />,
  },
  "/integrations": {
    component: <IntegrationsPage />,
  },
  "/delete-account": { component: <Navigate to={paths.settingsDelete()} /> },
  "/usage": { component: <Navigate to={paths.settingsUsage()} /> },
  "/settings": {
    auth: "required",
    children: {
      "/settings": { component: <Navigate to={paths.settingsAccount()} /> },
      "/settings/account": {
        auth: "required",
        component: <SettingsAccountPage />,
      },
      "/settings/auth": {
        auth: "required",
        component: <SettingsAuthPage />,
      },
      "/settings/billing": {
        auth: "require-team-owner",
        component: <SettingsBillingPage />,
      },
      "/settings/billing/subscription/cancel": {
        auth: "require-team-owner",
        component: <SettingsBillingPage cancel />,
      },
      "/settings/billing/trial/extend": {
        auth: "require-team-owner",
        component: <SettingsBillingPage trialExtend />,
      },
      "/settings/delete-account": {
        auth: "required",
        component: <SettingsDeletePage />,
      },
      "/settings/members/members": {
        auth: "require-team-owner",
        component: <SettingsTeamPage />,
      },
      "/settings/usage": {
        auth: "require-team-owner",
        component: <SettingsUsagePage />,
      },
      "/settings/integrations": {
        auth: "required",
        component: <SettingsIntegrationPage />,
      },
    },
    component: <SettingsPage />,
  },
  "/settings/api": {
    component: <SettingsApiPage />,
  },
  "/settings/billing/pay_now": {
    auth: "required",
    component: <InvoicePayNowPage />,
  },
  "settings/billing/trial-convert": {
    auth: "require-team-owner",
    component: <SettingsBillingTrialConvert />,
  },
  "/purchase": {
    children: {
      "/purchase": { component: <Redirect to={paths.purchase()} /> },
      "/purchase/checkout/:id": {
        auth: "required",
        authRequiredRedirectTo: "signup",
        component: <PurchaseCheckoutPage />,
      },
      "/purchase/return/stripe/:id": {
        component: <PurchaseReturnStripePage />,
      },
    },
    component: <PurchasePage />,
  },
  "/verify": {
    component: <VerifyPage />,
  },
  "/verify/bulk/:search_id/download": {
    auth: "required",
    component: <VerifyPage bulkDownload />,
  },
  "/extract/map": {
    component: <MapExtractorPage />,
  },
  "/extract/map/new": {
    auth: "required",
    component: <MapExtractorPage createModalOpen />,
  },
  "/extract/map/:search_id": {
    auth: "required",
    component: <MapExtractorPage />,
  },
  "/extract/map/:search_id/download": {
    auth: "required",
    component: <MapExtractorPage />,
  },
  "/email/unsub": {
    component: <EmailUnsubscribePage />,
  },
};

export default routeList;
